import { useState, useRef, useEffect } from 'react';
import { useTokens } from '../../../contexts/TokenContext';
import InsufficientTokensModal from '../../../components/feature/InsufficientTokensModal';
import PayPalSubscriptionModal from '../../../components/feature/PayPalSubscriptionModal';

interface Message {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  tags?: string[];
}

const dreamDatabase: Record<string, { meaning: string; luck: string; advice: string; tags: string[] }> = {
  '돼지': {
    meaning: '돼지 꿈은 전통적으로 재물과 풍요를 상징합니다. 살찐 돼지일수록 큰 재물이 들어올 조짐으로 해석됩니다.',
    luck: '재물운 ★★★★★ · 사업운 ★★★★☆',
    advice: '투자나 사업 확장을 고려해볼 좋은 시기입니다. 단, 돼지가 도망가거나 죽는 꿈이라면 반대로 재물 손실을 주의하세요.',
    tags: ['재물', '풍요', '행운'],
  },
  '뱀': {
    meaning: '뱀 꿈은 지혜, 변화, 재생을 상징합니다. 한국 전통 해몽에서는 큰 뱀을 잡거나 뱀이 몸을 감으면 큰 행운이 따르는 길몽입니다.',
    luck: '재물운 ★★★★☆ · 직업운 ★★★☆☆',
    advice: '뱀의 색깔과 행동이 중요합니다. 황금색 뱀은 재물, 검은 뱀은 위험 경고, 흰 뱀은 귀인 출현을 의미합니다.',
    tags: ['지혜', '변화', '재물'],
  },
  '물': {
    meaning: '물 꿈은 감정 상태와 잠재의식을 반영합니다. 맑고 잔잔한 물은 평화와 안정을, 거친 파도는 감정적 혼란이나 큰 변화를 암시합니다.',
    luck: '감정운 ★★★★☆ · 사업운 ★★★★☆',
    advice: '물이 넘치는 꿈은 감정이 넘칠 정도로 풍요로운 상황을, 물에 빠지는 꿈은 심리적 부담을 덜어낼 필요가 있음을 나타냅니다.',
    tags: ['감정', '변화', '잠재의식'],
  },
  '불': {
    meaning: '불 꿈은 강한 에너지와 변혁을 상징합니다. 활활 타오르는 불은 열정과 성공, 집에 불이 나는 꿈은 큰 변화나 재물 상승을 의미합니다.',
    luck: '사업운 ★★★★★ · 건강운 ★★★☆☆',
    advice: '불을 통제하는 꿈이라면 강한 의지와 리더십이 발휘될 시기입니다. 불에 타는 꿈은 정화와 새로운 시작을 의미합니다.',
    tags: ['변혁', '에너지', '성공'],
  },
  '하늘': {
    meaning: '하늘 꿈은 자유, 이상, 영적 성장을 상징합니다. 맑고 푸른 하늘은 밝은 미래와 성공, 구름 낀 하늘은 잠시의 시련을 암시합니다.',
    luck: '전체운 ★★★★★ · 직업운 ★★★★☆',
    advice: '하늘을 나는 꿈은 목표 달성과 높은 지위를 의미합니다. 별이 가득한 밤하늘 꿈은 귀인을 만날 조짐입니다.',
    tags: ['자유', '성공', '이상'],
  },
  '용': {
    meaning: '용은 한국 해몽에서 최고의 길몽 중 하나입니다. 용을 보거나 용이 하늘로 오르는 꿈은 크게 성공하고 높은 자리에 오를 것을 의미합니다.',
    luck: '전체운 ★★★★★ · 출세운 ★★★★★',
    advice: '용꿈은 큰 기회의 전조입니다. 태몽으로 나타나면 훌륭한 자녀를, 사업가에게는 대박의 신호, 직장인에게는 승진을 예고합니다.',
    tags: ['대길', '출세', '성공', '태몽'],
  },
  '죽음': {
    meaning: '죽음 꿈은 흉몽이 아니라 오히려 변화와 새로운 시작을 상징하는 경우가 많습니다. 자신이 죽는 꿈은 탈바꿈과 재생을 의미합니다.',
    luck: '변화운 ★★★★☆ · 재생운 ★★★★☆',
    advice: '죽음 꿈 후에는 큰 변화가 따라올 수 있습니다. 지인이 죽는 꿈은 그 사람과의 관계 변화나 오히려 오래 살 것을 뜻하기도 합니다.',
    tags: ['변화', '재생', '전환점'],
  },
  '이빨': {
    meaning: '이빨 빠지는 꿈은 불안과 자신감 부족, 또는 주변 가족의 건강 이상을 경고하는 꿈입니다.',
    luck: '건강운 ★★☆☆☆ · 대인운 ★★☆☆☆',
    advice: '이빨 꿈을 꿨다면 건강 검진을 받아보거나 가까운 가족의 안부를 확인해보세요. 심리적 스트레스를 해소하는 것도 중요합니다.',
    tags: ['건강', '불안', '가족'],
  },
  '돈': {
    meaning: '돈을 줍거나 받는 꿈은 재물운의 상승을 의미하지만, 반대로 지폐를 많이 받는 꿈은 반드시 길몽이 아닐 수 있습니다.',
    luck: '재물운 ★★★★☆ · 금전운 ★★★★☆',
    advice: '돈을 잃어버리는 꿈은 오히려 재물이 들어올 전조일 수 있습니다. 한국 전통 해몽에서는 반전 해석이 자주 적용됩니다.',
    tags: ['재물', '금전', '풍요'],
  },
  '시험': {
    meaning: '시험 꿈은 현실에서 받고 있는 압박과 평가에 대한 불안을 반영합니다. 시험에 합격하는 꿈은 목표 달성, 낙방은 준비 부족 경고입니다.',
    luck: '학업운 ★★★☆☆ · 직업운 ★★★☆☆',
    advice: '시험 꿈을 자주 꾼다면 현실에서 스트레스를 해소할 방법을 찾는 것이 좋습니다.',
    tags: ['불안', '학업', '도전'],
  },
  '집': {
    meaning: '집은 자아와 내면 세계를 상징합니다. 새 집을 짓거나 이사하는 꿈은 생활 환경의 변화나 새로운 시작을 의미합니다.',
    luck: '생활운 ★★★★☆ · 이동운 ★★★★☆',
    advice: '집이 무너지는 꿈은 관계나 기반이 흔들리고 있다는 신호입니다.',
    tags: ['자아', '변화', '안정'],
  },
  '비': {
    meaning: '비 꿈은 정화와 새로운 시작을 상징합니다. 비를 맞으면 근심 해소, 맑은 봄비는 재물과 행운을 상징합니다.',
    luck: '재물운 ★★★★☆ · 건강운 ★★★★☆',
    advice: '폭우를 맞는 꿈은 갑작스러운 재물이나 기회가 올 징조입니다.',
    tags: ['정화', '재물', '시작'],
  },
  '전남친': {
    meaning: '전 남자친구(전남친)가 꿈에 나오는 것은 과거의 감정이 아직 완전히 정리되지 않았거나, 그 관계에서 배운 교훈이 현재 상황에 필요하다는 신호입니다. 반드시 미련이 있다는 뜻은 아닙니다.',
    luck: '감정운 ★★★☆☆ · 연애운 ★★★★☆',
    advice: '전남친 꿈은 오히려 새로운 연애를 준비하는 무의식의 신호인 경우가 많습니다.',
    tags: ['감정정리', '연애', '새출발'],
  },
  '전여친': {
    meaning: '전 여자친구(전여친)가 꿈에 나타나는 것은 과거의 감정적 패턴이나 해결하지 못한 감정이 현재 상황과 연결되어 있음을 나타냅니다.',
    luck: '감정운 ★★★☆☆ · 자기성찰 ★★★★★',
    advice: '과거 연애에서 반복되는 패턴이 있다면 지금이 그것을 인식하고 변화할 기회입니다.',
    tags: ['감정정리', '자기성찰', '성장'],
  },
  '바람': {
    meaning: '파트너가 바람피는 꿈은 실제 불륜을 예고하는 것이 아니라, 관계에서의 불안감이나 신뢰에 대한 갈망을 상징합니다.',
    luck: '관계운 ★★★☆☆ · 신뢰운 ★★★★☆',
    advice: '바람피는 꿈을 꾼 후에는 실제 파트너와의 대화를 늘리는 것이 좋습니다.',
    tags: ['불안', '신뢰', '소통'],
  },
  '남편': {
    meaning: '남편이 꿈에 나오는 경우 그 상황에 따라 해석이 달라집니다. 남편과 행복한 꿈은 결혼 생활의 안정을 뜻하고, 갈등 꿈은 소통 부재를 반영합니다.',
    luck: '부부운 ★★★★☆ · 가정운 ★★★☆☆',
    advice: '꿈 속 남편의 표정과 행동이 현실 관계의 거울입니다.',
    tags: ['부부관계', '소통', '가정'],
  },
  '아내': {
    meaning: '아내가 꿈에 등장하는 것은 가정과 안정에 대한 무의식적 관심을 나타냅니다.',
    luck: '가정운 ★★★★☆ · 부부운 ★★★★☆',
    advice: '꿈에서 아내가 슬퍼하거나 멀어지는 장면이 나왔다면 파트너에게 더 많은 관심을 보여줄 때입니다.',
    tags: ['가정', '사랑', '안정'],
  },
  '가족': {
    meaning: '가족이 꿈에 나오는 것은 안정·유대감·근원적 에너지와 연결됩니다.',
    luck: '가정운 ★★★★☆ · 건강운 ★★★★☆',
    advice: '꿈에 자주 가족이 나타난다면 현실에서 가족과의 유대를 강화할 시기입니다.',
    tags: ['가족', '안정', '유대감'],
  },
  '이별': {
    meaning: '이별하는 꿈은 변화와 전환의 신호입니다. 슬픈 이별 꿈은 현재 상황에서 벗어나려는 욕구이며, 아프지 않은 이별은 자유와 새로운 시작의 신호입니다.',
    luck: '변화운 ★★★★☆ · 독립운 ★★★☆☆',
    advice: '이별 꿈은 반드시 실제 이별을 의미하지 않습니다. 불필요한 것을 정리할 시기라는 메시지로 해석하세요.',
    tags: ['변화', '이별', '새출발'],
  },
  '결혼': {
    meaning: '결혼하는 꿈은 결합과 헌신, 새로운 단계의 시작을 상징합니다.',
    luck: '연애운 ★★★★★ · 사업운 ★★★★☆',
    advice: '결혼 꿈은 연인이 없는 분에게도 새로운 만남이나 파트너십의 전조입니다.',
    tags: ['사랑', '결합', '새출발'],
  },
  '쫓기': {
    meaning: '누군가에게 쫓기는 꿈은 현실에서 피하고 싶은 문제나 감정적 부담을 상징합니다.',
    luck: '건강운 ★★★☆☆ · 심리운 ★★☆☆☆',
    advice: '회피보다 직면이 더 나은 해결책입니다.',
    tags: ['스트레스', '해결', '심리'],
  },
  '추락': {
    meaning: '높은 곳에서 떨어지는 꿈은 불안감이나 통제력 상실에 대한 두려움을 나타냅니다.',
    luck: '안정운 ★★☆☆☆ · 심리운 ★★★☆☆',
    advice: '추락 꿈은 중요한 전환점 전에 자주 나타납니다. 현실의 기반을 다지는 것이 중요합니다.',
    tags: ['불안', '통제', '기반'],
  },
};

function analyzeRelationshipDream(dreamText: string): { content: string; tags: string[] } | null {
  if (dreamText.includes('전남친') || dreamText.includes('전 남자친구') || dreamText.includes('전남자친구')) {
    return {
      content: '전남친이 꿈에 나타났군요. 많은 분들이 이런 꿈 때문에 걱정하시는데, 사실 미련의 신호보다는 성장의 메시지인 경우가 더 많습니다.\n\n✨ 꿈해몽 분석\n전남친 꿈은 세 가지 의미 중 하나입니다: ① 그 관계에서 배운 교훈이 현재 상황에 필요하다 ② 비슷한 유형의 새로운 인연이 다가오고 있다 ③ 과거 감정의 최종 정리 시기가 됐다.\n\n🌟 운세 포인트\n연애운 ★★★★☆ · 감정운 ★★★☆☆\n\n🌙 동양 해몽 관점\n사주 관점에서 전 연인이 꿈에 나오는 것은 현재 감정의 대운이 활성화됐다는 신호입니다.\n\n💡 조언\n꿈에서 전남친과의 분위기가 좋았다면 곧 비슷한 좋은 에너지를 가진 새 인연이 등장할 가능성이 높습니다. 분위기가 불편했다면 과거 패턴을 반복하지 말라는 경고 메시지입니다.',
      tags: ['전남친', '연애', '새출발', '감정정리'],
    };
  }

  if (dreamText.includes('전여친') || dreamText.includes('전 여자친구') || dreamText.includes('전여자친구')) {
    return {
      content: '전여친이 꿈에 등장했군요. 이 꿈의 메시지는 생각보다 훨씬 긍정적일 수 있습니다.\n\n✨ 꿈해몽 분석\n전여친 꿈은 주로 두 가지를 의미합니다: ① 현재의 나 자신이 그 시절보다 성장했음을 확인하는 무의식의 회고 ② 비슷한 에너지를 가진 새로운 인연이 곧 들어온다는 전조.\n\n🌟 운세 포인트\n자기성찰 ★★★★★ · 연애운 ★★★★☆\n\n💡 조언\n꿈 속 전여친과 어떤 상황이었는지가 중요합니다. 다시 사귀는 장면이라면 과거 관계의 어떤 요소가 그리웠는지 돌아보세요. 그것이 다음 관계에서 추구해야 할 요소를 알려줍니다.',
      tags: ['전여친', '연애', '자기성찰', '성장'],
    };
  }

  if (dreamText.includes('바람') || dreamText.includes('불륜') || dreamText.includes('외도')) {
    return {
      content: '파트너가 바람피는 꿈을 꾸셨군요. 먼저 안심하세요 — 이 꿈은 실제 불륜의 예고가 아닙니다!\n\n✨ 꿈해몽 분석\n바람피는 꿈은 관계에서의 불안감이나 소외감을 반영합니다. 파트너의 관심을 충분히 받지 못하고 있거나, 관계에서 자신의 가치를 확인받고 싶은 욕구가 있을 때 이런 꿈이 나타납니다.\n\n🌟 운세 포인트\n관계운 ★★★☆☆ · 소통운 ★★★★☆\n\n🌙 심리학적 해석\n융 심리학에서 이런 꿈은 "관계 불안 처리 메커니즘"입니다. 무의식이 불안을 꿈으로 표출해 낮 동안 그 불안을 느끼지 않도록 처리하는 것입니다.\n\n💡 조언\n이 꿈은 파트너와의 소통을 강화하라는 신호입니다. 요즘 대화가 줄었거나 함께하는 시간이 적었다면 의식적으로 늘려보세요.',
      tags: ['바람', '관계불안', '소통', '신뢰'],
    };
  }

  if (dreamText.includes('죽') || dreamText.includes('사망') || dreamText.includes('돌아가')) {
    return {
      content: '누군가 죽는 꿈을 꾸셨군요. 깜짝 놀라셨겠지만, 동양 해몽에서 죽음 꿈은 오히려 좋은 의미인 경우가 많습니다!\n\n✨ 꿈해몽 분석\n가족이나 지인이 죽는 꿈은 그 사람이 오래 살 것이라는 역설적 길몽으로 해석됩니다. 또한 그 사람과의 관계가 새로운 단계로 발전하거나, 그 사람에 관한 걱정이 해소될 신호이기도 합니다.\n\n🌟 운세 포인트\n변화운 ★★★★☆ · 전환점 ★★★★★\n\n🌙 자신이 죽는 꿈이라면?\n본인이 죽는 꿈은 특히 강력한 길몽입니다. 현재의 나쁜 상황이 완전히 끝나고 새로운 시작이 이루어진다는 의미입니다.\n\n💡 조언\n죽음 꿈 후에는 큰 변화를 두려워하지 마세요. 오히려 변화를 적극적으로 수용하고 새로운 시작을 준비하는 것이 좋습니다.',
      tags: ['죽음꿈', '길몽', '변화', '새출발'],
    };
  }

  return null;
}

function analyzeDream(dreamText: string): { content: string; tags: string[] } {
  const relationshipResult = analyzeRelationshipDream(dreamText);
  if (relationshipResult) return relationshipResult;

  const lowerText = dreamText.toLowerCase();
  const matchedKeys: string[] = [];

  for (const key of Object.keys(dreamDatabase)) {
    if (dreamText.includes(key)) {
      matchedKeys.push(key);
    }
  }

  if (matchedKeys.length === 0) {
    const generalResponses = [
      {
        keywords: ['날다', '비행', '하늘을 날'],
        content: '하늘을 나는 꿈은 자유에 대한 열망과 현실의 제약에서 벗어나고 싶은 마음을 반영합니다.\n\n✨ 꿈해몽 분석\n하늘을 자유롭게 나는 꿈은 목표를 향해 높이 도약할 준비가 되었음을 의미합니다.\n\n🌟 운세 포인트\n직업운 ★★★★☆ · 도전운 ★★★★★\n\n💡 조언\n지금이야말로 오래 미뤄온 꿈이나 계획을 실행에 옮길 최적의 타이밍입니다!',
        tags: ['자유', '성장', '도전'],
      },
      {
        keywords: ['쫓기다', '도망', '추격'],
        content: '누군가에게 쫓기는 꿈은 현실에서 피하고 싶은 문제나 감정적 부담을 상징합니다.\n\n✨ 꿈해몽 분석\n쫓기는 꿈은 스트레스와 압박감의 표현입니다.\n\n🌟 운세 포인트\n건강운 ★★★☆☆ · 심리운 ★★☆☆☆\n\n💡 조언\n회피보다 직면이 더 나은 해결책입니다. 마음속에 쌓인 스트레스를 털어낼 방법을 찾아보세요.',
        tags: ['스트레스', '해결', '심리'],
      },
    ];

    for (const resp of generalResponses) {
      if (resp.keywords.some((k) => lowerText.includes(k))) {
        return { content: resp.content, tags: resp.tags };
      }
    }

    return {
      content: `꿈 내용을 면밀히 살펴보겠습니다.\n\n✨ 꿈해몽 분석\n꿈에 등장하는 요소들은 모두 잠재의식의 메시지입니다. 말씀하신 꿈 속 상황을 기준으로 분석하면, 현재 삶에서 중요한 변화의 기로에 서 있거나 중요한 결정을 앞두고 있을 가능성이 높습니다.\n\n🌟 동양 해몽 관점\n자미두수와 사주 오행으로 보면, 꿈에 나타나는 이미지는 현재 활성화된 대운의 기운과 연결됩니다.\n\n🌙 서양 심리학 관점\n융(Jung)의 원형 이론에 따르면, 꿈은 개인 무의식과 집단 무의식이 표출되는 공간입니다.\n\n💡 조언\n꿈을 꾼 직후의 느낌과 감정이 중요합니다. 더 자세한 해몽을 위해 꿈 속 세부 내용을 말씀해 주시면 더 정확하게 분석해드릴 수 있어요!`,
      tags: ['잠재의식', '변화'],
    };
  }

  const mainKey = matchedKeys[0];
  const data = dreamDatabase[mainKey];
  let content = `**${mainKey}** 꿈을 꾸셨군요! 전통 한국 해몽 + 심리학적 분석으로 풀어드리겠습니다.\n\n`;
  content += `✨ 꿈해몽 분석\n${data.meaning}\n\n`;
  content += `🌟 운세 포인트\n${data.luck}\n\n`;

  if (matchedKeys.length > 1) {
    content += `🔮 복합 꿈 해석\n`;
    matchedKeys.slice(1).forEach((k) => {
      content += `• ${k}: ${dreamDatabase[k].meaning.split('.')[0]}.\n`;
    });
    content += '\n';
  }

  content += `💡 조언\n${data.advice}`;

  const allTags = [...new Set(matchedKeys.flatMap((k) => dreamDatabase[k].tags))];
  return { content, tags: allTags };
}

function buildFollowUp(userMsg: string): string {
  if (userMsg.includes('태몽') || userMsg.includes('임신') || userMsg.includes('아기')) {
    return '태몽 분석을 해드리겠습니다!\n\n✨ 태몽 해석\n태몽은 임신이나 중요한 탄생을 예고하는 특별한 꿈입니다. 용이나 뱀, 호랑이 같은 강한 동물이 나타나면 리더십과 카리스마가 강한 아이를, 꽃이나 빛나는 구슬은 온화하고 총명한 아이를 예고합니다.\n\n🌙 전통 해석\n• 용 태몽: 크게 성공할 아이 (최길)\n• 호랑이 태몽: 강하고 용맹한 아이\n• 복숭아·사과: 건강하고 영리한 아이\n• 별이나 달: 예술·학문에서 빛날 아이\n\n꿈 속에 구체적으로 무엇이 나타났나요?';
  }

  if (userMsg.includes('반복') || userMsg.includes('자주') || userMsg.includes('항상')) {
    return '반복되는 꿈에 대해 분석해드리겠습니다.\n\n✨ 반복몽의 의미\n같은 꿈이 반복된다면 해결되지 않은 심리적 문제나 중요한 메시지가 있다는 신호입니다.\n\n🌙 해결 방법\n1. 꿈 일기를 써서 패턴을 파악하세요\n2. 꿈 속에서 두려운 대상과 대화를 시도해보세요\n3. 현실에서 해결하지 못한 상황이 없는지 돌아보세요\n\n반복되는 꿈의 구체적인 내용을 알려주시면 더 깊이 분석해드릴게요!';
  }

  if (userMsg.includes('악몽') || userMsg.includes('무섭') || userMsg.includes('공포')) {
    return '악몽에 대해 분석해드리겠습니다.\n\n✨ 악몽의 의미\n악몽은 두려움이나 불안감의 표현입니다. 동양 해몽에서는 악몽 후에 오히려 좋은 일이 생긴다는 "전화위복" 해석이 많습니다.\n\n💡 악몽 후 좋은 징조\n• 뱀에 물리는 꿈 → 재물이 들어올 징조\n• 물에 빠지는 꿈 → 위기 돌파, 재생의 기운\n• 죽는 꿈 → 새로운 시작, 변화의 신호\n\n구체적인 악몽 내용을 알려주시면 더 자세히 풀어드릴게요!';
  }

  return '꿈에 대해 더 자세히 분석해드리겠습니다.\n\n꿈 속 구체적인 상황이나 느낌, 등장인물 등을 더 말씀해주시면 더욱 정확한 해몽이 가능합니다. 아래 빠른 질문 버튼으로 원하시는 항목을 선택해보세요!';
}

const QUICK_PROMPT_GROUPS = [
  {
    label: '💔 관계·연애',
    color: 'pink',
    prompts: [
      { label: '전남친 꿈', icon: 'ri-heart-broken-line', q: '전남친이 꿈에 나왔어요. 무슨 의미인가요?' },
      { label: '전여친 꿈', icon: 'ri-heart-broken-2-line', q: '전여친이 꿈에 자꾸 나와요.' },
      { label: '바람피는 꿈', icon: 'ri-spy-line', q: '남편이 바람피는 꿈을 꿨어요.' },
      { label: '누가 죽는 꿈', icon: 'ri-skull-2-line', q: '가족이 죽는 꿈을 꿨어요. 흉몽인가요?' },
      { label: '결혼하는 꿈', icon: 'ri-heart-add-line', q: '결혼하는 꿈을 꿨어요.' },
      { label: '이별하는 꿈', icon: 'ri-walk-line', q: '연인과 이별하는 꿈을 꿨어요.' },
    ],
  },
  {
    label: '🌟 길몽',
    color: 'gold',
    prompts: [
      { label: '돼지 꿈', icon: 'ri-star-smile-line', q: '돼지 꿈을 꿨어요.' },
      { label: '용 꿈', icon: 'ri-sparkling-2-line', q: '용 꿈을 꿨어요. 길몽인가요?' },
      { label: '돈 줍는 꿈', icon: 'ri-money-cny-circle-line', q: '돈이 나오는 꿈을 꿨는데 해몽해주세요.' },
      { label: '태몽 해석', icon: 'ri-seedling-line', q: '태몽에 대해 해석해주세요.' },
    ],
  },
  {
    label: '😨 흉몽·반복',
    color: 'red',
    prompts: [
      { label: '이빨 빠지는 꿈', icon: 'ri-emotion-unhappy-line', q: '이빨이 빠지는 꿈을 꿨어요.' },
      { label: '쫓기는 꿈', icon: 'ri-run-line', q: '쫓기는 꿈을 자꾸 꿔요.' },
      { label: '추락하는 꿈', icon: 'ri-arrow-down-double-line', q: '높은 곳에서 떨어지는 꿈을 꿨어요.' },
      { label: '반복되는 꿈', icon: 'ri-loop-left-line', q: '같은 꿈이 반복되는데 무슨 의미인가요?' },
    ],
  },
];

export default function DreamChatInterface() {
  const { tokens, deductTokens, hasEnoughTokens } = useTokens();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      role: 'assistant',
      content: '안녕하세요! 저는 꿈해몽 AI 상담사입니다. 꿈 속에서 본 것, 느낀 감정, 상황을 자세히 말씀해주시면 전통 한국 해몽 + 동양 오행 + 서양 심리학을 종합하여 풀이해 드립니다. 🌙\n\n아래 빠른 질문 버튼을 누르거나 직접 꿈 내용을 입력해주세요!',
      timestamp: new Date().toISOString(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showInsufficientModal, setShowInsufficientModal] = useState(false);
  const [requiredTokens, setRequiredTokens] = useState(0);
  const [activeGroup, setActiveGroup] = useState(0);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const subscriptionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 1분 후 구독 모달 표시
  useEffect(() => {
    subscriptionTimerRef.current = setTimeout(() => {
      setShowSubscriptionModal(true);
    }, 60000);
    return () => {
      if (subscriptionTimerRef.current) clearTimeout(subscriptionTimerRef.current);
    };
  }, []);

  const totalUserMessages = messages.filter((m) => m.role === 'user').length;
  const usedTokens = totalUserMessages * 5;
  const maxTokens = 500;
  const usagePercentage = (usedTokens / maxTokens) * 100;

  const handleSend = () => {
    if (!inputValue.trim()) return;
    const MESSAGE_COST = 5;
    if (!hasEnoughTokens(MESSAGE_COST)) {
      setRequiredTokens(MESSAGE_COST);
      setShowInsufficientModal(true);
      return;
    }
    deductTokens(MESSAGE_COST);
    const userText = inputValue.trim();
    const userMessage: Message = {
      id: messages.length + 1,
      role: 'user',
      content: userText,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    setTimeout(() => {
      const isFirstUserMsg = messages.filter((m) => m.role === 'user').length === 0;
      let responseContent: string;
      let tags: string[] = [];

      if (isFirstUserMsg) {
        const result = analyzeDream(userText);
        responseContent = result.content;
        tags = result.tags;
      } else {
        responseContent = buildFollowUp(userText);
      }

      const aiMessage: Message = {
        id: messages.length + 2,
        role: 'assistant',
        content: responseContent,
        timestamp: new Date().toISOString(),
        tags,
      };
      setMessages((prev) => [...prev, aiMessage]);
      setIsTyping(false);
    }, 2000);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <div className="max-w-4xl mx-auto">
        <div className="bg-gradient-to-br from-primary-dark/90 via-primary-dark/80 to-mystic-dark/90 backdrop-blur-xl rounded-3xl border-2 border-primary-gold/40 overflow-hidden shadow-[0_0_60px_rgba(255,215,0,0.15)]">
          {/* Chat header */}
          <div className="flex items-center gap-3 px-6 py-4 border-b border-primary-gold/20 bg-gradient-to-r from-primary-dark/60 to-mystic-dark/60">
            <div className="w-10 h-10 flex items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-600 to-violet-700 shadow-lg shadow-purple-500/50">
              <i className="ri-moon-fill text-white text-lg"></i>
            </div>
            <div>
              <p className="text-white text-sm font-bold">꿈해몽 AI 상담</p>
              <p className="text-purple-300/60 text-xs">한국 전통 해몽 · 오행 · 심리학 종합풀이</p>
            </div>
            <div className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/30">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
              <span className="text-green-400 text-xs font-medium">온라인</span>
            </div>
          </div>

          {/* Messages area */}
          <div className="h-[52vh] sm:h-[540px] overflow-y-auto p-4 sm:p-8 space-y-6 custom-scrollbar">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 sm:gap-4 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
              >
                <div
                  className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center shadow-lg ${
                    message.role === 'assistant'
                      ? 'bg-gradient-to-br from-indigo-500 via-purple-600 to-violet-700 shadow-purple-500/50'
                      : 'bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 shadow-amber-500/50'
                  }`}
                >
                  <i
                    className={`${
                      message.role === 'assistant' ? 'ri-moon-fill text-white' : 'ri-user-3-fill text-primary-dark'
                    } text-lg sm:text-xl`}
                  ></i>
                </div>

                <div className={`flex-1 max-w-[90%] sm:max-w-[80%] ${message.role === 'user' ? 'text-right' : 'text-left'}`}>
                  <div
                    className={`inline-block rounded-3xl px-5 sm:px-7 py-4 sm:py-5 shadow-xl transition-all duration-300 hover:scale-[1.01] ${
                      message.role === 'assistant'
                        ? 'bg-gradient-to-br from-primary-dark/95 via-mystic-dark/90 to-primary-dark/95 border-2 border-primary-gold/40 text-white shadow-primary-gold/20'
                        : 'bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 text-primary-dark shadow-amber-500/30'
                    }`}
                  >
                    <p className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap font-medium">
                      {message.content}
                    </p>
                  </div>

                  {message.tags && message.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {message.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 rounded-full bg-primary-gold/10 border border-primary-gold/30 text-primary-gold text-xs font-medium"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-600 to-violet-700 flex items-center justify-center shadow-lg shadow-purple-500/50 flex-shrink-0">
                  <i className="ri-moon-fill text-white text-lg sm:text-xl"></i>
                </div>
                <div className="bg-gradient-to-br from-primary-dark/95 via-mystic-dark/90 to-primary-dark/95 border-2 border-primary-gold/40 rounded-3xl px-6 sm:px-8 py-4 sm:py-5 shadow-xl shadow-primary-gold/20">
                  <div className="flex gap-2">
                    <div className="w-2.5 h-2.5 bg-primary-gold rounded-full animate-bounce shadow-lg shadow-primary-gold/50"></div>
                    <div className="w-2.5 h-2.5 bg-primary-gold rounded-full animate-bounce shadow-lg shadow-primary-gold/50" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2.5 h-2.5 bg-primary-gold rounded-full animate-bounce shadow-lg shadow-primary-gold/50" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="border-t-2 border-primary-gold/40 p-4 sm:p-6 bg-gradient-to-br from-primary-dark/60 via-mystic-dark/50 to-primary-dark/60 backdrop-blur-lg">
            {/* Token usage bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs sm:text-sm text-mystic-purple mb-2">
                <span className="font-medium">토큰 사용량</span>
                <span className="font-bold">{usedTokens} / {maxTokens}</span>
              </div>
              <div className="h-2 bg-primary-dark/80 rounded-full overflow-hidden border border-primary-gold/20 shadow-inner">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 rounded-full transition-all duration-500 shadow-[0_0_10px_rgba(255,215,0,0.5)]"
                  style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Quick prompt groups */}
            <div className="mb-4">
              {/* Group tabs */}
              <div className="flex items-center gap-1 mb-3 overflow-x-auto pb-1">
                {QUICK_PROMPT_GROUPS.map((group, idx) => (
                  <button
                    key={group.label}
                    onClick={() => setActiveGroup(idx)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                      activeGroup === idx
                        ? group.color === 'pink'
                          ? 'bg-pink-500/20 border-pink-500/50 text-pink-300'
                          : group.color === 'gold'
                          ? 'bg-primary-gold/20 border-primary-gold/50 text-primary-gold'
                          : 'bg-red-500/20 border-red-500/50 text-red-300'
                        : 'bg-white/5 border-white/10 text-purple-300/60 hover:text-purple-200'
                    }`}
                  >
                    {group.label}
                  </button>
                ))}
              </div>

              {/* Prompts for active group */}
              <div className="flex flex-wrap gap-2">
                {QUICK_PROMPT_GROUPS[activeGroup].prompts.map((q) => {
                  const g = QUICK_PROMPT_GROUPS[activeGroup];
                  const colorClass =
                    g.color === 'pink'
                      ? 'border-pink-500/25 text-pink-200 hover:bg-pink-500/15 hover:border-pink-400/60 hover:text-pink-200'
                      : g.color === 'gold'
                      ? 'border-primary-gold/25 text-purple-200 hover:bg-primary-gold/15 hover:border-primary-gold/60 hover:text-primary-gold'
                      : 'border-red-500/25 text-red-200/80 hover:bg-red-500/15 hover:border-red-400/60 hover:text-red-200';

                  return (
                    <button
                      key={q.label}
                      onClick={() => {
                        setInputValue(q.q);
                        setTimeout(() => inputRef.current?.focus(), 50);
                      }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${colorClass}`}
                    >
                      <i className={`${q.icon} text-xs`}></i>
                      <span>{q.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Input & send */}
            <div className="flex gap-3 items-end">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="꿈 내용을 자세히 말씀해주세요... (Enter로 전송, Shift+Enter 줄바꿈)"
                  rows={2}
                  className="w-full px-5 sm:px-6 py-3.5 sm:py-4 bg-primary-dark/70 border-2 border-primary-gold/40 rounded-2xl text-white placeholder-gray-400 focus:border-primary-gold focus:shadow-[0_0_20px_rgba(255,215,0,0.3)] focus:outline-none text-sm sm:text-base transition-all duration-300 resize-none custom-scrollbar"
                />
              </div>
              <button
                onClick={handleSend}
                className="w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0 rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 text-primary-dark hover:shadow-[0_0_30px_rgba(255,215,0,0.6)] hover:scale-110 transition-all duration-300 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-send-plane-fill text-lg sm:text-xl"></i>
              </button>
            </div>
            <p className="text-center text-purple-300/40 text-xs mt-3">메시지 1건당 5토큰 소모 · 잔여 {tokens} 토큰</p>
          </div>
        </div>
      </div>

      <InsufficientTokensModal
        isOpen={showInsufficientModal}
        onClose={() => setShowInsufficientModal(false)}
        requiredTokens={requiredTokens}
        currentTokens={tokens}
      />
      <PayPalSubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        chatType="dream"
      />
    </>
  );
}
